import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
} from '@ant-design/icons'
import appStore from '@stores/appStore'
import assistantStore from '@stores/assistantStore'
import { Alert, message } from 'antd'
import { observer } from 'mobx-react'
import { useEffect, useRef, useState } from 'react'

import { useAssistantSession } from './AssistantSessionContext'
import { conversationTime, groupConversations } from './conversationGroups'
import { ERROR_MESSAGES } from './errorMessages'
import { KW } from './ui'

/** How long after the last keystroke the history is filtered. */
const FILTER_DELAY_MS = 300

/** The longest title a conversation can be given (AssistantConsts.MaxConversationTitleLength). */
const MAX_TITLE_LENGTH = 60

/**
 * Conversation history: the user's conversations a page at a time ("Load more"), filtered by
 * title on the server. Deleting asks for confirmation inside the row itself (no pop-over), so
 * nothing overflows the panel and the list never jumps. Renaming happens in the row too: Enter
 * saves, Escape puts the title back.
 */
const ConversationList = () => {
  const session = useAssistantSession()
  const {
    conversations,
    conversationsTotal,
    conversationsHasMore,
    loadingMoreConversations,
    conversationFilter,
    conversationsFiltering,
  } = assistantStore
  const { activeConversationId, loadingConversationId, errorCode } = session
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // What is typed; the list follows a moment later (see FILTER_DELAY_MS).
  const [filterText, setFilterText] = useState(conversationFilter)
  // Once the filter box was used it stays on screen, whatever the list holds: a user who clears
  // it must not have the box they are typing in taken away (and the focus with it).
  const [filterUsed, setFilterUsed] = useState(conversationFilter.trim() !== '')
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameText, setRenameText] = useState('')
  const [savingRename, setSavingRename] = useState(false)
  const renameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    cancelRef.current?.focus()
  }, [confirmingId])

  useEffect(() => {
    renameRef.current?.focus()
    renameRef.current?.select()
  }, [renamingId])

  useEffect(() => {
    if (filterText.trim() === assistantStore.conversationFilter.trim()) {
      return undefined
    }
    const timer = setTimeout(
      () => assistantStore.filterConversations(filterText),
      FILTER_DELAY_MS
    )
    return () => clearTimeout(timer)
  }, [filterText])

  const filtering = conversationFilter.trim() !== ''
  // The filter the list is waiting for: typed but not sent yet, or sent and not answered yet.
  const pending =
    conversationsFiltering || filterText.trim() !== conversationFilter.trim()

  const changeFilter = (text: string) => {
    setFilterUsed(true)
    setFilterText(text)
  }

  const deleteConversation = async (id: string) => {
    setDeletingId(id)
    try {
      await assistantStore.deleteConversation(id)
      setConfirmingId(null)
    } catch {
      message.error('The conversation could not be deleted. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  const startRename = (id: string, title: string) => {
    setConfirmingId(null)
    setRenameText(title)
    setRenamingId(id)
  }

  const saveRename = async (id: string, currentTitle: string) => {
    const title = renameText.replace(/\s+/g, ' ').trim()
    if (!title || savingRename) {
      return
    }
    if (title === currentTitle) {
      setRenamingId(null)
      return
    }
    setSavingRename(true)
    try {
      await assistantStore.renameConversation(id, title)
      setRenamingId(null)
    } catch {
      // The row stays open with what was typed: nothing is lost, and the old title is still
      // the conversation's.
      message.error('The conversation could not be renamed. Please try again.')
    } finally {
      setSavingRename(false)
    }
  }

  const backToChat = (
    <button
      type="button"
      className="kw-history-back"
      onClick={session.toggleHistory}
    >
      <ArrowLeftOutlined /> Back to chat
    </button>
  )

  // The history of a user who has none - not of one whose filter matches nothing, or who has
  // just cleared the filter: they keep the box they typed in.
  if (!conversations.length && !filterUsed && !pending) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <MessageOutlined style={{ fontSize: 28, color: KW.line2 }} />
        <div className="text-sm font-bold" style={{ color: KW.ink }}>
          No conversations yet
        </div>
        <div className="text-[13px]" style={{ color: KW.muted }}>
          Your chats with the Assistant will appear here.
        </div>
        {backToChat}
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="flex flex-shrink-0 items-center justify-between px-4 py-[10px]"
        style={{ borderBottom: `1px solid ${KW.line}` }}
      >
        <span className="text-[13px] font-extrabold" style={{ color: KW.ink }}>
          Conversations{' '}
          <span style={{ color: KW.muted }}>
            ({Math.max(conversationsTotal, conversations.length)})
          </span>
        </span>
        {backToChat}
      </div>

      <div className="flex-shrink-0 px-3 pt-3">
        <input
          type="search"
          className="kw-history-filter"
          value={filterText}
          maxLength={100}
          placeholder="Filter by title"
          aria-label="Filter conversations by title"
          onChange={e => changeFilter(e.target.value)}
        />
      </div>

      {/* The chat (where errors normally show) is hidden behind the history. */}
      {errorCode === 'CONVERSATION_LOAD_FAILED' && (
        <Alert
          className="mx-3 mt-3 flex-shrink-0"
          type="warning"
          showIcon
          closable
          onClose={session.dismissError}
          message={ERROR_MESSAGES.CONVERSATION_LOAD_FAILED}
        />
      )}

      <div
        className="kw-scroll kw-history min-h-0 flex-1 overflow-y-auto px-2 pb-3"
        aria-busy={pending || undefined}
      >
        {groupConversations(conversations.slice()).map(group => (
          <section key={group.label} aria-label={group.label}>
            <div className="kw-history-group">{group.label}</div>
            {group.items.map(item => {
              const title = item.title || 'Untitled conversation'
              const active = item.id === activeConversationId
              const opening = item.id === loadingConversationId
              // Still being answered in another view (a placement's pane): it opens here once
              // that answer is over, so no two views show it with different content.
              const answeringElsewhere =
                !active &&
                assistantStore.isConversationBusyElsewhere(item.id, session)

              if (item.id === confirmingId) {
                return (
                  <div
                    key={item.id}
                    className="kw-history-row kw-history-confirm"
                    role="alertdialog"
                    aria-label="Delete this conversation?"
                    onKeyDown={e => e.key === 'Escape' && setConfirmingId(null)}
                  >
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-[13px] font-bold"
                        style={{ color: KW.red }}
                      >
                        Delete this conversation?
                      </div>
                      <div
                        className="kw-history-title"
                        style={{ color: KW.muted }}
                      >
                        {title}
                      </div>
                    </div>
                    <button
                      ref={cancelRef}
                      type="button"
                      className="kw-history-btn"
                      onClick={() => setConfirmingId(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="kw-history-btn kw-history-btn-danger"
                      disabled={deletingId === item.id}
                      onClick={() => deleteConversation(item.id)}
                    >
                      {deletingId === item.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                )
              }

              if (item.id === renamingId) {
                return (
                  <form
                    key={item.id}
                    className="kw-history-row kw-history-renaming"
                    aria-label="Rename this conversation"
                    onSubmit={e => {
                      e.preventDefault()
                      saveRename(item.id, item.title ?? '')
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Escape') {
                        e.stopPropagation()
                        setRenamingId(null)
                      }
                    }}
                  >
                    <input
                      ref={renameRef}
                      type="text"
                      className="kw-history-filter"
                      value={renameText}
                      maxLength={MAX_TITLE_LENGTH}
                      aria-label="Conversation title"
                      disabled={savingRename}
                      onChange={e => setRenameText(e.target.value)}
                    />
                    <button
                      type="button"
                      className="kw-history-btn"
                      disabled={savingRename}
                      onClick={() => setRenamingId(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="kw-history-btn kw-history-btn-primary"
                      disabled={savingRename || !renameText.trim()}
                    >
                      {savingRename ? 'Saving…' : 'Save'}
                    </button>
                  </form>
                )
              }

              return (
                <div
                  key={item.id}
                  className={`kw-history-row${active ? ' is-active' : ''}`}
                >
                  <button
                    type="button"
                    className="kw-history-open"
                    aria-current={active ? 'true' : undefined}
                    aria-busy={opening || answeringElsewhere || undefined}
                    // Not while an answer is coming in: it belongs to the open conversation.
                    disabled={session.isTurnRunning || answeringElsewhere}
                    onClick={() => session.selectConversation(item.id)}
                  >
                    <span className="kw-history-title">{title}</span>
                    <span
                      className="kw-history-time"
                      role={opening ? 'status' : undefined}
                    >
                      {opening
                        ? 'Opening…'
                        : answeringElsewhere
                        ? 'Answering…'
                        : conversationTime(
                            item.lastMessageAt,
                            appStore.currentUser?.dateFormat
                          )}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="kw-history-delete kw-history-rename"
                    title="Rename conversation"
                    aria-label={`Rename conversation: ${title}`}
                    onClick={() => startRename(item.id, item.title ?? '')}
                  >
                    <EditOutlined />
                  </button>
                  <button
                    type="button"
                    className="kw-history-delete"
                    title="Delete conversation"
                    aria-label={`Delete conversation: ${title}`}
                    onClick={() => setConfirmingId(item.id)}
                  >
                    <DeleteOutlined />
                  </button>
                </div>
              )
            })}
          </section>
        ))}

        {/* Only once the server has answered for the filter in the box: said (and announced)
          while the answer is on its way, it would be said about a list nobody has seen. */}
        {!conversations.length && !pending && (
          <div
            role="status"
            className="px-3 py-6 text-center text-[13px]"
            style={{ color: KW.muted }}
          >
            {filtering ? (
              <>
                No conversation title contains &ldquo;
                {conversationFilter.trim()}
                &rdquo;.
              </>
            ) : (
              'No conversations yet.'
            )}
          </div>
        )}

        {conversationsHasMore && conversations.length > 0 && (
          <div className="flex justify-center px-3 pt-3">
            <button
              type="button"
              className="kw-history-btn"
              disabled={loadingMoreConversations}
              onClick={assistantStore.loadMoreConversations}
            >
              {loadingMoreConversations ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default observer(ConversationList)
